import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import { showErrorMessage, showSuccessMessage } from '@actions/messageconfirmation';

const FIRST_PAGE = 1;
const NOT_FOUND_STATUS = 404;

const DEFAULT_STALE_STATUSES = [NOT_FOUND_STATUS];

/**
 * Actions on the rows of a server-side paginated list, for the lists whose fetch lives inside an
 * effect and is therefore not callable from the outside: the reload goes through the returned
 * `refreshIndex`, which the caller adds to its effect deps.
 *
 * `performDeletion` removes the row and is configured once, at the hook level: a list only ever
 * deletes one resource. `performRowUpdate` rewrites the row in place — a status transition for
 * instance — and is configured per call, since one list can carry several such actions, each with
 * its own request and toasters.
 *
 * A status listed in `staleStatuses` means the displayed row no longer reflects the server — gone
 * already, or no longer in a state that allows the action — so the list is resynchronized on top
 * of the error toaster instead of keeping a row the user can act on.
 *
 * @param {Object} params
 * @param {Function} params.deleteResource - Deletion service call, receives the row id
 * @param {string} params.deleteSuccessMessage - Toaster shown once the row is deleted
 * @param {string} params.deleteErrorMessage - Toaster shown when the deletion fails
 * @param {Array<number>} [params.staleStatuses] - HTTP statuses meaning the row was already stale
 * @param {number} params.displayedRowCount - Number of rows currently displayed
 * @param {number} params.currentPage - Page currently displayed
 * @param {Function} params.setCurrentPage - Page setter of the list
 * @param {Function} params.setIsLoading - Loading setter of the list
 * @param {Function} [params.setSelectedRows] - Selection setter, emptied before each action
 *
 * @returns {{ refreshIndex: number, performDeletion: Function, performRowUpdate: Function }}
 */
export const useListRowActions = ({
  deleteResource,
  deleteSuccessMessage,
  deleteErrorMessage,
  staleStatuses = DEFAULT_STALE_STATUSES,
  displayedRowCount,
  currentPage,
  setCurrentPage,
  setIsLoading,
  setSelectedRows,
}) => {
  const dispatch = useDispatch();

  const [refreshIndex, setRefreshIndex] = useState(0);

  const refreshList = useCallback(() => {
    setRefreshIndex((index) => index + 1);
  }, []);

  /**
   * Removing the last row of a page beyond the first one leaves that page empty: step back one page
   * instead of refreshing in place, otherwise the user lands on an empty view.
   */
  const reloadListAfterRemoval = useCallback(() => {
    if (displayedRowCount === 1 && currentPage > FIRST_PAGE) {
      setCurrentPage(currentPage - 1);

      return;
    }

    refreshList();
  }, [displayedRowCount, currentPage, setCurrentPage, refreshList]);

  /**
   * Shared body of every row action: the request, its toasters, then the list resynchronization —
   * `reloadList` being what changes between removing a row and rewriting it.
   */
  const performRowAction = useCallback(
    async ({ request, successMessage, errorMessage, reloadList }) => {
      setIsLoading(true);

      // The rows are about to be resynchronized: drop the selection to stay in sync.
      if (setSelectedRows) {
        setSelectedRows([]);
      }

      try {
        await request();

        dispatch(showSuccessMessage(successMessage));

        reloadList();
      } catch (error) {
        dispatch(showErrorMessage(errorMessage));

        if (staleStatuses.includes(error?.response?.status)) {
          reloadList();

          return;
        }

        setIsLoading(false);
      }
    },
    [staleStatuses, setIsLoading, setSelectedRows, dispatch],
  );

  const performDeletion = useCallback(
    ({ id }) =>
      performRowAction({
        request: () => deleteResource(id),
        successMessage: deleteSuccessMessage,
        errorMessage: deleteErrorMessage,
        reloadList: reloadListAfterRemoval,
      }),
    [
      performRowAction,
      deleteResource,
      deleteSuccessMessage,
      deleteErrorMessage,
      reloadListAfterRemoval,
    ],
  );

  /**
   * Rewrite a row in place. The list is refetched on its current page without stepping back: the
   * row may well still be displayed, and a page the update left empty — the row left the current
   * tab — is already brought back to the first page by the list itself.
   */
  const performRowUpdate = useCallback(
    ({ request, successMessage, errorMessage }) =>
      performRowAction({
        request,
        successMessage,
        errorMessage,
        reloadList: refreshList,
      }),
    [performRowAction, refreshList],
  );

  return { refreshIndex, performDeletion, performRowUpdate };
};
